import type { ReactNode } from 'react';

/**
 * A headline number that doubles as the filter for its rows. A gap with
 * nothing outstanding reads as done (muted), one with people in it as a job.
 */
export function CountTile({
  label,
  value,
  sub,
  tone = 'neutral',
  active = false,
  onClick,
  action,
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: 'neutral' | 'gap';
  active?: boolean;
  /** Absent = an information tile, not a filter. */
  onClick?: () => void;
  /** A small control under the number (e.g. "Remind"). */
  action?: ReactNode;
}) {
  const hot = tone === 'gap' && value > 0;
  const body = (
    <>
      <span
        className={`block font-display text-2xl leading-none md:text-3xl ${
          hot ? 'text-warning' : tone === 'gap' ? 'text-cream-300/40' : 'text-cream-50'
        }`}
      >
        {value}
        {sub && <span className="ml-1 font-body text-xs text-cream-300/50">{sub}</span>}
      </span>
      <span className="mt-1 block text-[10px] uppercase leading-tight tracking-wide text-cream-300/70 md:text-[11px]">
        {label}
      </span>
    </>
  );
  const frame = `rounded-lg border px-2.5 py-2 text-left transition md:px-3 md:py-3 ${
    active
      ? 'border-gold-400 bg-gold-600/10 ring-1 ring-gold-400/60'
      : 'border-ink-800 bg-ink-900/70'
  }`;
  if (!onClick) {
    return (
      <div className={frame}>
        {body}
        {action}
      </div>
    );
  }
  return (
    <div className={`${frame} hover:border-gold-500/60`}>
      <button
        type="button"
        aria-pressed={active}
        onClick={onClick}
        className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60"
      >
        {body}
      </button>
      {action}
    </div>
  );
}
